import React, { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import PhoneInput, { isValidPhoneNumber, getCountryCallingCode } from 'react-phone-number-input';
import { 
  BookOpen, Brain, MessageCircle, Target, Award, Clock, 
  Monitor, Users, HelpCircle, Sparkles, ArrowRight, CheckCircle,
  GraduationCap, Calendar, Timer
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

// Timezone list
const TIMEZONES = [
  { value: 'Pacific/Midway', label: '(GMT-11:00) Midway Island' },
  { value: 'Pacific/Honolulu', label: '(GMT-10:00) Hawaii' },
  { value: 'America/Anchorage', label: '(GMT-09:00) Alaska' },
  { value: 'America/Los_Angeles', label: '(GMT-08:00) Pacific Time (US & Canada)' },
  { value: 'America/Denver', label: '(GMT-07:00) Mountain Time (US & Canada)' },
  { value: 'America/Chicago', label: '(GMT-06:00) Central Time (US & Canada)' },
  { value: 'America/New_York', label: '(GMT-05:00) Eastern Time (US & Canada)' },
  { value: 'America/Caracas', label: '(GMT-04:00) Caracas' },
  { value: 'America/Sao_Paulo', label: '(GMT-03:00) Brasilia' },
  { value: 'Atlantic/South_Georgia', label: '(GMT-02:00) Mid-Atlantic' },
  { value: 'Atlantic/Azores', label: '(GMT-01:00) Azores' },
  { value: 'Europe/London', label: '(GMT+00:00) London, Dublin' },
  { value: 'Europe/Paris', label: '(GMT+01:00) Paris, Berlin, Rome' },
  { value: 'Europe/Helsinki', label: '(GMT+02:00) Helsinki, Cairo' },
  { value: 'Europe/Moscow', label: '(GMT+03:00) Moscow, Kuwait' },
  { value: 'Asia/Dubai', label: '(GMT+04:00) Dubai, Abu Dhabi' },
  { value: 'Asia/Karachi', label: '(GMT+05:00) Karachi, Islamabad' },
  { value: 'Asia/Kolkata', label: '(GMT+05:30) Mumbai, New Delhi' },
  { value: 'Asia/Dhaka', label: '(GMT+06:00) Dhaka' },
  { value: 'Asia/Bangkok', label: '(GMT+07:00) Bangkok, Jakarta' },
  { value: 'Asia/Singapore', label: '(GMT+08:00) Singapore, Hong Kong' },
  { value: 'Asia/Tokyo', label: '(GMT+09:00) Tokyo, Seoul' },
  { value: 'Australia/Sydney', label: '(GMT+10:00) Sydney, Melbourne' },
  { value: 'Pacific/Noumea', label: '(GMT+11:00) New Caledonia' },
  { value: 'Pacific/Auckland', label: '(GMT+12:00) Auckland, Wellington' }
];

const GRADES = [
  'Grade 7', 'Grade 8', 'Grade 9', 'Grade 10', 
  'Grade 11', 'Grade 12', 'IB Year 1', 'IB Year 2',
  'A-Level Year 1', 'A-Level Year 2', 'Other'
];

const CURRICULA = ['IB', 'IGCSE', 'A-Levels', 'O-Levels', 'Other'];
const SUBJECTS = ['Physics', 'Mathematics', 'Chemistry'];

const ComplimentarySession = () => {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    timezone: '',
    grade: '',
    curriculum: '',
    subject: '',
    topic: '',
    preferred_date: '',
    preferred_time: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const scrollToForm = () => {
    document.getElementById('session-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.full_name || !formData.email || !formData.phone) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!isValidPhoneNumber(formData.phone || '')) {
      toast.error('Please enter a valid phone number');
      return;
    }

    if (!formData.timezone || !formData.grade || !formData.curriculum || !formData.subject) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!formData.preferred_date || !formData.preferred_time) {
      toast.error('Please select your preferred date and time');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/complimentary-session/request`, formData);
      setSubmitted(true);
      toast.success('Your request has been submitted successfully!');
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to submit request. Please try again.';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    { icon: Brain, title: 'Understand complex concepts clearly', desc: 'Break down difficult topics into simple, digestible explanations' },
    { icon: BookOpen, title: 'Experience concept-driven teaching', desc: 'Learn through our unique methodology that builds deep understanding' },
    { icon: MessageCircle, title: 'Ask questions directly', desc: 'Get your doubts cleared by expert instructors in real-time' },
    { icon: Target, title: 'Evaluate if PhysicsCo is right for you', desc: 'Make an informed decision about your academic journey' },
    { icon: Award, title: 'Gain confidence', desc: 'Build strong foundations in physics and mathematics' }
  ];

  const sessionStructure = [
    { icon: Clock, title: 'Duration', desc: '90-120 minutes of focused learning' },
    { icon: Monitor, title: 'Online Session', desc: 'Conducted via video conferencing' },
    { icon: BookOpen, title: 'Concept Explanation', desc: 'Clear and structured topic coverage' },
    { icon: HelpCircle, title: 'Interactive Problem Solving', desc: 'Practice with guided examples' },
    { icon: Users, title: 'Personalized Doubt Clearing', desc: 'One-on-one attention to your questions' }
  ];

  const targetAudience = [
    { name: 'IB Students', desc: 'HL & SL Physics and Mathematics' },
    { name: 'IGCSE Students', desc: 'Physics and Additional Mathematics' },
    { name: 'A-Level Students', desc: 'Physics and Further Mathematics' },
    { name: 'O-Level Students', desc: 'Physics and E-Mathematics' },
    { name: 'Advanced Learners', desc: 'Students preparing for advanced topics' }
  ];

  if (submitted) {
    return (
      <div className="py-24 min-h-[70vh] flex items-center">
        <div className="container mx-auto px-4 md:px-6 max-w-xl">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="glass-card p-8 md:p-12 text-center"
          >
            <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-400" />
            </div>
            <h1 className="font-outfit text-3xl font-bold text-white mb-4">
              Request Submitted Successfully!
            </h1>
            <p className="text-slate-400 mb-6">
              Thank you for your interest in PhysicsCo. Our team will review your request and contact you soon to confirm your complimentary session schedule.
            </p>
            <p className="text-electric-cyan text-sm mb-8">
              A confirmation email has been sent to {formData.email}
            </p>
            <Button onClick={() => window.location.href = '/'} className="btn-primary">
              Return to Home
            </Button>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-12 md:py-16">
      {/* Hero Section */}
      <section className="container mx-auto px-4 md:px-6 mb-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center max-w-4xl mx-auto"
        >
          <div className="inline-flex items-center gap-2 bg-electric-cyan/10 border border-electric-cyan/30 rounded-full px-4 py-2 mb-6">
            <Sparkles className="w-4 h-4 text-electric-cyan" />
            <span className="text-electric-cyan text-sm font-medium">Free of Cost</span>
          </div>
          
          <h1 className="font-outfit text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6" data-testid="complimentary-hero-title">
            Experience PhysicsCo with a{' '}
            <span className="text-electric-cyan">Complimentary Session</span>
          </h1>
          
          <p className="text-lg md:text-xl text-slate-300 mb-6 max-w-3xl mx-auto">
            Join a complimentary session and experience how PhysicsCo simplifies complex concepts.
          </p>
          
          <div className="bg-surface-navy/50 border border-electric-cyan/20 rounded-xl p-4 mb-8 max-w-2xl mx-auto">
            <p className="text-electric-cyan font-medium">
              Complimentary session is free of cost and designed to help you understand our teaching methodology.
            </p>
          </div>
          
          <Button 
            onClick={scrollToForm} 
            className="btn-primary text-lg px-8 py-6 h-auto"
            data-testid="book-session-btn"
          >
            Book Complimentary Session <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </motion.div>
      </section>

      {/* Why Attend Section */}
      <section className="container mx-auto px-4 md:px-6 mb-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="font-outfit text-3xl md:text-4xl font-bold text-white mb-4">
            Why Attend a Complimentary Session?
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto">
            Discover the benefits of experiencing our teaching approach firsthand
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="glass-card p-6 hover:border-electric-cyan/30 transition-all"
            >
              <div className="w-12 h-12 rounded-xl bg-electric-cyan/10 flex items-center justify-center mb-4">
                <benefit.icon className="w-6 h-6 text-electric-cyan" />
              </div>
              <h3 className="font-outfit text-lg font-semibold text-white mb-2">{benefit.title}</h3>
              <p className="text-slate-400 text-sm">{benefit.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* What Happens Section */}
      <section className="container mx-auto px-4 md:px-6 mb-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="font-outfit text-3xl md:text-4xl font-bold text-white mb-4">
            What Happens in the Session?
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto">
            A structured session designed to give you a complete learning experience
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-4 max-w-6xl mx-auto">
          {sessionStructure.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="glass-card p-5 text-center"
            >
              <div className="w-10 h-10 rounded-lg bg-electric-cyan/10 flex items-center justify-center mx-auto mb-3">
                <item.icon className="w-5 h-5 text-electric-cyan" />
              </div>
              <h3 className="font-outfit font-semibold text-white mb-1">{item.title}</h3>
              <p className="text-slate-400 text-xs">{item.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Who Should Attend Section */}
      <section className="container mx-auto px-4 md:px-6 mb-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="font-outfit text-3xl md:text-4xl font-bold text-white mb-4">
            Who Should Attend?
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto">
            Our complimentary sessions are designed for students across various curricula
          </p>
        </motion.div>

        <div className="flex flex-wrap justify-center gap-4 max-w-4xl mx-auto">
          {targetAudience.map((audience, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="glass-card px-6 py-4 flex items-center gap-3"
            >
              <GraduationCap className="w-5 h-5 text-electric-cyan" />
              <div>
                <h3 className="font-outfit font-semibold text-white">{audience.name}</h3>
                <p className="text-slate-400 text-xs">{audience.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Registration Form Section */}
      <section id="session-form" className="container mx-auto px-4 md:px-6 mb-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-3xl mx-auto"
        >
          <div className="text-center mb-8">
            <h2 className="font-outfit text-3xl md:text-4xl font-bold text-white mb-4">
              Request Your Complimentary Session
            </h2>
            <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 max-w-xl mx-auto">
              <p className="text-green-400 font-medium">
                No payment is required to request a complimentary session.
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="glass-card p-6 md:p-8 space-y-6">
            {/* Personal Information */}
            <div className="space-y-4">
              <h3 className="font-outfit text-lg font-semibold text-white border-b border-surface-navy pb-2">
                Personal Information
              </h3>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-2">Full Name *</label>
                  <Input
                    value={formData.full_name}
                    onChange={e => handleChange('full_name', e.target.value)}
                    placeholder="Enter your full name"
                    className="input-dark"
                    required
                    data-testid="session-name"
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-2">Email Address *</label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={e => handleChange('email', e.target.value)}
                    placeholder="Enter your email"
                    className="input-dark"
                    required
                    data-testid="session-email"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-slate-400 mb-2">Phone Number *</label>
                <PhoneInput
                  international
                  defaultCountry="SG"
                  value={formData.phone}
                  onChange={value => handleChange('phone', value || '')}
                  className="phone-input-dark"
                  data-testid="session-phone"
                />
              </div>

              <div>
                <label className="block text-sm text-slate-400 mb-2">Your Time Zone *</label>
                <Select value={formData.timezone} onValueChange={v => handleChange('timezone', v)}>
                  <SelectTrigger className="input-dark" data-testid="session-timezone">
                    <SelectValue placeholder="Select your timezone" />
                  </SelectTrigger>
                  <SelectContent className="bg-deep-navy border-surface-navy max-h-60">
                    {TIMEZONES.map(tz => (
                      <SelectItem key={tz.value} value={tz.value} className="text-white hover:bg-surface-navy">
                        {tz.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Academic Information */}
            <div className="space-y-4">
              <h3 className="font-outfit text-lg font-semibold text-white border-b border-surface-navy pb-2">
                Academic Information
              </h3>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-2">Current Grade *</label>
                  <Select value={formData.grade} onValueChange={v => handleChange('grade', v)}>
                    <SelectTrigger className="input-dark" data-testid="session-grade">
                      <SelectValue placeholder="Select your grade" />
                    </SelectTrigger>
                    <SelectContent className="bg-deep-navy border-surface-navy">
                      {GRADES.map(grade => (
                        <SelectItem key={grade} value={grade} className="text-white hover:bg-surface-navy">
                          {grade}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-2">Curriculum *</label>
                  <Select value={formData.curriculum} onValueChange={v => handleChange('curriculum', v)}>
                    <SelectTrigger className="input-dark" data-testid="session-curriculum">
                      <SelectValue placeholder="Select curriculum" />
                    </SelectTrigger>
                    <SelectContent className="bg-deep-navy border-surface-navy">
                      {CURRICULA.map(c => (
                        <SelectItem key={c} value={c} className="text-white hover:bg-surface-navy">
                          {c}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label className="block text-sm text-slate-400 mb-2">Subject of Interest *</label>
                <Select value={formData.subject} onValueChange={v => handleChange('subject', v)}>
                  <SelectTrigger className="input-dark" data-testid="session-subject">
                    <SelectValue placeholder="Select subject" />
                  </SelectTrigger>
                  <SelectContent className="bg-deep-navy border-surface-navy">
                    {SUBJECTS.map(s => (
                      <SelectItem key={s} value={s} className="text-white hover:bg-surface-navy">
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm text-slate-400 mb-2">Topic for Complimentary Demo Session</label>
                <Input
                  value={formData.topic}
                  onChange={e => handleChange('topic', e.target.value)}
                  placeholder="e.g., Kinematics, Calculus, Electromagnetism"
                  className="input-dark"
                  data-testid="session-topic"
                />
              </div>
            </div>

            {/* Schedule Preferences */}
            <div className="space-y-4">
              <h3 className="font-outfit text-lg font-semibold text-white border-b border-surface-navy pb-2">
                Schedule Preferences
              </h3>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-2">Preferred Date *</label>
                  <Input
                    type="date"
                    value={formData.preferred_date}
                    onChange={e => handleChange('preferred_date', e.target.value)}
                    className="input-dark"
                    min={new Date().toISOString().split('T')[0]}
                    required
                    data-testid="session-date"
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-2">Preferred Time *</label>
                  <Input
                    type="time"
                    value={formData.preferred_time}
                    onChange={e => handleChange('preferred_time', e.target.value)}
                    className="input-dark"
                    required
                    data-testid="session-time"
                  />
                </div>
              </div>
              
              <p className="text-slate-500 text-xs">
                * Your selected time will be converted to Singapore Time (SGT) for scheduling purposes.
              </p>
            </div>

            {/* Additional Message */}
            <div>
              <label className="block text-sm text-slate-400 mb-2">Additional Message (Optional)</label>
              <Textarea
                value={formData.message}
                onChange={e => handleChange('message', e.target.value)}
                placeholder="Any specific topics or questions you'd like us to cover?"
                className="input-dark"
                rows={4}
                data-testid="session-message"
              />
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={loading}
              className="btn-primary w-full h-12 text-base"
              data-testid="session-submit-btn"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-deep-blue"></div>
              ) : (
                <>
                  Request Complimentary Session <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>

            <p className="text-center text-slate-500 text-sm">
              By submitting this form, you agree to be contacted by PhysicsCo regarding your session request.
            </p>
          </form>
        </motion.div>
      </section>
    </div>
  );
};

export default ComplimentarySession;
